<template>
  <div class="flash-feed-overlay">
    <div class="flash-feed-container">
      <FlashTopicSelector
        :items="commodityList"
        :selected="selectedCommodity"
        @change="onTopicChange"
      />
      <div
        ref="viewport"
        class="cards-viewport"
        tabindex="0"
        @keydown="onKeyDown"
        @wheel="onWheel"
        @pointerdown="onPointerDown"
        @click.capture="onDragClickGuard"
      >
        <div class="cards-track" :style="trackStyle">
          <div
            v-for="(card, i) in cards"
            :key="card._id"
            class="card-slot"
          >
            <FlashCard :item="card" :active="i === currentIndex" />
          </div>
          <div
            v-if="cards.length && !hasMore"
            class="card-slot end-slot"
          >
            <div class="end-state">
              <b-icon icon="check2-circle" font-scale="2" class="mb-3"></b-icon>
              <span>Todas as notícias carregadas</span>
            </div>
          </div>
        </div>
        <div
          v-if="loading && !cards.length"
          class="loading-state"
        >
          <b-spinner variant="light" class="mr-2"></b-spinner>
          <span>Carregando...</span>
        </div>
        <div
          v-if="!cards.length && !loading"
          class="empty-state"
        >
          <b-icon icon="newspaper" font-scale="3" class="mb-3"></b-icon>
          <p>Nenhuma notícia encontrada</p>
          <button
            class="btn btn-outline-light btn-sm"
            @click="selectedCommodity = 0; loadFeed()"
          >
            Limpar filtros
          </button>
        </div>
        <div
          v-if="loading && cards.length"
          class="prefetch-spinner"
        >
          <b-spinner variant="light" small></b-spinner>
        </div>
      </div>
      <div
        v-if="cards.length"
        class="progress-rail"
      >
        <span
          v-for="(dot, i) in progressDots"
          :key="i"
          class="progress-dot"
          :class="{ active: dot === currentIndex }"
        ></span>
      </div>
      <div
        v-if="cards.length"
        class="nav-buttons"
      >
        <button
          class="nav-btn"
          :disabled="currentIndex <= 0"
          @click="goPrev"
          title="Anterior"
        >
          <b-icon icon="chevron-up"></b-icon>
        </button>
        <button
          class="nav-btn"
          :disabled="currentIndex >= totalSlides - 1"
          @click="goNext"
          title="Próximo"
        >
          <b-icon icon="chevron-down"></b-icon>
        </button>
      </div>
    </div>
    <button
      class="close-btn"
      @click="goBack"
      title="Fechar"
    >
      <b-icon icon="x-lg"></b-icon>
    </button>
  </div>
</template>

<script>
import FlashCard from './FlashCard.vue'
import FlashTopicSelector from './FlashTopicSelector.vue'
import flashService from '../../../providers/flashService'
import { COMMODITY_LIST } from './mockData'

const TRANSITION_MS = 380
const DRAG_RESISTANCE = 0.35
const SWIPE_THRESHOLD_RATIO = 0.18
const PREFETCH_BUFFER = 2

export default {
  components: { FlashCard, FlashTopicSelector },
  data() {
    return {
      commodityList: COMMODITY_LIST,
      selectedCommodity: 0,
      cards: [],
      page: 1,
      totalPages: 1,
      loading: false,
      hasMore: true,
      currentIndex: 0,
      limit: 10,
      isLocked: false,
      suppressNextClick: false,
      drag: {
        active: false,
        moved: false,
        startY: 0,
        dy: 0,
      },
    }
  },

  computed: {
    totalSlides() {
      return this.cards.length + (this.cards.length && !this.hasMore ? 1 : 0)
    },
    trackStyle() {
      const base = -(this.currentIndex * 100)
      if (this.drag.active && this.drag.moved) {
        return {
          transform: `translateY(calc(${base}% + ${this.drag.dy}px))`,
          transition: 'none',
        }
      }
      return {
        transform: `translateY(${base}%)`,
        transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      }
    },
    progressDots() {
      const windowSize = 7
      const total = this.totalSlides
      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i)
      }
      let start = this.currentIndex - Math.floor(windowSize / 2)
      start = Math.max(0, Math.min(start, total - windowSize))
      return Array.from({ length: windowSize }, (_, i) => start + i)
    },
  },

  created() {
    this.loadFeed()
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.viewport) this.$refs.viewport.focus()
    })
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
  },
  methods: {
    loadFeed() {
      this.page = 1
      this.cards = []
      this.hasMore = true
      this.currentIndex = 0
      this.fetchCards()
    },
    async fetchCards() {
      if (this.loading || !this.hasMore) return
      this.loading = true
      try {
        const resp = await flashService.getFlashFeed({
          page: this.page,
          limit: this.limit,
          commodity: this.selectedCommodity,
          originCode: this.$store.state.currentCommodity?.originCode,
        })
        this.cards.push(...resp.data)
        this.totalPages = resp.totalPages
        this.hasMore = this.page < resp.totalPages
        this.page += 1
      } catch {
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    maybePrefetch() {
      if (this.hasMore && !this.loading && this.currentIndex >= this.cards.length - 1 - PREFETCH_BUFFER) {
        this.fetchCards()
      }
    },
    navigateTo(index) {
      const clamped = Math.max(0, Math.min(index, this.totalSlides - 1))
      if (clamped === this.currentIndex) return
      this.currentIndex = clamped
      this.maybePrefetch()
    },
    goPrev() {
      this.navigateTo(this.currentIndex - 1)
    },
    goNext() {
      this.navigateTo(this.currentIndex + 1)
    },
    onTopicChange(code) {
      this.selectedCommodity = code
      this.currentIndex = 0
      this.loadFeed()
    },
    lockBriefly() {
      this.isLocked = true
      window.setTimeout(() => {
        this.isLocked = false
      }, TRANSITION_MS)
    },
    onWheel(e) {
      if (this.isLocked) return
      if (Math.abs(e.deltaY) < 12) return
      this.lockBriefly()
      if (e.deltaY > 0) this.goNext()
      else this.goPrev()
    },
    onKeyDown(e) {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault()
        this.goNext()
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault()
        this.goPrev()
      } else if (e.key === 'Home') {
        e.preventDefault()
        this.navigateTo(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        this.navigateTo(this.totalSlides - 1)
      }
    },
    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      this.drag.active = true
      this.drag.moved = false
      this.drag.startY = e.clientY
      this.drag.dy = 0
      window.addEventListener('pointermove', this.onPointerMove)
      window.addEventListener('pointerup', this.onPointerUp)
    },
    onPointerMove(e) {
      if (!this.drag.active) return
      let dy = e.clientY - this.drag.startY
      if (Math.abs(dy) > 6) this.drag.moved = true
      const atStart = this.currentIndex <= 0
      const atEnd = this.currentIndex >= this.totalSlides - 1
      if ((dy > 0 && atStart) || (dy < 0 && atEnd)) {
        dy *= DRAG_RESISTANCE
      }
      this.drag.dy = dy
    },
    onPointerUp() {
      window.removeEventListener('pointermove', this.onPointerMove)
      window.removeEventListener('pointerup', this.onPointerUp)
      if (!this.drag.active) return
      this.drag.active = false
      if (this.drag.moved) {
        // depois de um arraste real, o navegador ainda dispara um "click" fantasma na posição
        // de origem (antes do card deslizar visualmente) - isso cai em cima do play/gallery-tap
        // do card que acabamos de sair. Marca pra engolir esse clique (ver onDragClickGuard).
        this.suppressNextClick = true
        const viewportHeight = this.$refs.viewport ? this.$refs.viewport.clientHeight : window.innerHeight
        const threshold = viewportHeight * SWIPE_THRESHOLD_RATIO
        if (this.drag.dy < -threshold) this.goNext()
        else if (this.drag.dy > threshold) this.goPrev()
      }
      this.drag.dy = 0
      this.drag.moved = false
    },
    onDragClickGuard(e) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false
        e.stopPropagation()
        e.preventDefault()
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push('/')
      }
    },
  },
}
</script>

<style>
.flash-feed-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  background: #000;
  overflow: hidden;
}

.flash-feed-container {
  position: relative;
  width: 100%;
  height: 100vh;
  margin: 0 auto;
}

@media (min-width: 576px) {
  .flash-feed-container {
    max-width: 460px;
  }
}

.cards-viewport {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: none;
  touch-action: none;
  user-select: none;
}

.cards-track {
  width: 100%;
  height: 100%;
  will-change: transform;
}

.card-slot {
  width: 100%;
  height: 100%;
}

.end-slot {
  display: flex;
}

.loading-state,
.prefetch-spinner {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  pointer-events: none;
}

.loading-state {
  top: 0;
  bottom: 0;
}

.prefetch-spinner {
  bottom: 24px;
  opacity: 0.7;
}

.end-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.empty-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
}

.progress-rail {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 15;
}

.progress-dot {
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.25);
  transition: background 0.25s ease, height 0.25s ease;
}

.progress-dot.active {
  background: #fff;
  height: 22px;
}

.nav-buttons {
  position: absolute;
  right: 8px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 20;
}

@media (min-width: 576px) {
  .nav-buttons {
    right: 12px;
    gap: 12px;
  }
}

.nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s, transform 0.15s;
}

.nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.6);
}

.nav-btn:active:not(:disabled) {
  transform: scale(0.9);
}

.nav-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.close-btn {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 20;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s, transform 0.15s;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.6);
}

.close-btn:active {
  transform: scale(0.9);
}
</style>
